<template>
  <div id="app" class="mimo-app" :style="chatAreaStyle">
    <!-- 应用头部 -->
<!--    <header class="mimo-header" :style="headerStyle">-->
    <header class="mimo-header">
      <div class="header-left">
        <!-- AI头像显示 -->
        <div class="header-avatar" v-if="settings.aiAvatar || settings.aiAvatarImage" :style="{background: settings.aiAvatarImage ? 'transparent' : getAvatarBackground()}">
          <img v-if="settings.aiAvatarImage" :src="settings.aiAvatarImage" class="header-avatar-img" alt="AI头像">
          <span v-else>{{ settings.aiAvatar }}</span>
        </div>
        <!-- 标题区域 -->
        <div class="mimo-title">
          <span class="header-icon">{{ settings.headerIcon }}</span>
          <span class="header-text">{{ settings.headerTitle }}</span>
        </div>
      </div>
      <div class="header-right">
        <!-- 用户头像显示 -->
        <div class="header-avatar user-avatar" v-if="settings.userAvatar || settings.userAvatarImage" :style="{background: settings.userAvatarImage ? 'transparent' : getUserAvatarBackground()}">
          <img v-if="settings.userAvatarImage" :src="settings.userAvatarImage" class="header-avatar-img" alt="用户头像">
          <span v-else>{{ settings.userAvatar }}</span>
        </div>
        <!-- 操作按钮 -->
        <div class="header-actions">
          <button @click="showSettings = true" class="icon-btn">⚙️</button>
          <button @click="showHistory = true" class="icon-btn">📚</button>
        </div>
      </div>
    </header>

    <!-- 主要内容区域 -->
    <main class="main-content">
      <!-- 聊天区域 -->
      <div class="chat-area" ref="chatArea">
        <ChatMessage
          v-for="(msg, index) in messages"
          :key="index"
          :message="msg"
          :settings="settings"
          :lastMessage="index === messages.length - 1"
          @retry="handleRetry"
          @scroll-to-bottom="scrollToBottom"
        />
        
        <!-- 欢迎语（首次使用） -->
        <div v-if="messages.length === 0" class="welcome-message">
          <div class="welcome-card">
            <h3>嗨～我是小明同学！</h3>
            <p>你的贴心AI助手，随时为你解答所有问题，你也可以向我倾诉。</p>
            <div class="quick-start">
              <button @click="quickStart('☹️ 心情不好求安慰 😭')">☹️ 心情不好求安慰 😭</button>
              <button @click="quickStart('🥰 讲个笑话吧 🤗')">🥰 讲个笑话吧 🤗</button>
              <button @click="quickStart('🤔 有什么好吃的推荐吗')">🤔 有什么好吃的推荐吗</button>
            </div>
          </div>
        </div>
      </div>

      <!-- 输入控制器 -->
      <ChatController
        @send="handleSendMessage"
        :settings="settings"
        :lastUserMessage="lastUserMessage"
      />
    </main>

    <!-- 设置弹窗 -->
    <div v-if="showSettings" class="modal-overlay" @click="showSettings = false">
      <div class="settings-modal" @click.stop>
        <SettingsPanel
          @close="showSettings = false"
          @settings-changed="handleSettingsChange"
        />
      </div>
    </div>

    <!-- 历史记录弹窗 -->
    <div v-if="showHistory" class="modal-overlay" @click="showHistory = false">
      <div class="history-modal" @click.stop>
        <HistoryPanel @close="showHistory = false" @load="loadHistory" />
      </div>
    </div>
  </div>
</template>

<script>
import ChatMessage from './components/ChatMessage.vue'
import ChatController from './components/ChatController.vue'
import SettingsPanel from './modules/settings/SettingsPanel.vue'
import HistoryPanel from './modules/history/HistoryPanel.vue'

export default {
  name: 'App',
  components: {
    ChatMessage,
    ChatController,
    SettingsPanel,
    HistoryPanel
  },
  
  data() {
    return {
      messages: [],
      showSettings: false,
      showHistory: false,
      conversationId: null,
      identity: '',
      lastUserMessage: '',
      settings: {
        voiceAssistant: false,
        tone: '亲切',
        autoSuggestions: true,
        notifications: true,
        productUpdates: false,
        saveHistory: true,
        dataCollection: false,
        identity: '',
        // 新增动画和显示设置
        animationEnabled: true,
        animationSpeed: 'normal', // slow, normal, fast
        showActionButtons: true,
        // 新增形象设置
        characterName: '',
        characterAge: '',
        characterGender: '',
        characterJob: '',
        characterPersonality: '',
        characterStyle: '',
        // 新增样式自定义设置
        headerIcon: '🤖',
        headerTitle: '小明同学',
        aiAvatar: '🤖',
        userAvatar: '👤',
        chatBackground: '#FFF8F3',
        // 新增图片设置
        aiAvatarImage: '',
        userAvatarImage: '',
        backgroundImage: '',
        // 新增对话框样式设置 - 微信风格默认值
        userInputStyle: {
          backgroundColor: '#E8F5E9',
          textColor: '#000000',
          opacity: 100,
          borderRadius: '16px',
          borderColor: '#C8E6C9',
          borderWidth: '1px'
        },
        userHeaderStyle: {
          opacity: 100,
        },
        aiOutputStyle: {
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          opacity: 100,
          borderRadius: '16px',
          borderColor: '#E5E5E5',
          borderWidth: '1px'
        }
      }
    }
  },
  
  computed: {
    // 头部样式
    headerStyle() {
      return {
        background: this.settings.chatBackground.startsWith('url')
          ? this.settings.chatBackground
          : 'rgba(255, 255, 255, 0.95)'
      }
    },
    
    // 聊天区域背景样式
    chatAreaStyle() {
      if (this.settings.chatBackground.startsWith('url')) {
        return {
          background: this.settings.chatBackground,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }
      } else {
        return {
          background: this.settings.chatBackground
        }
      }
    }
  },
  
  methods: {
    // 获取AI头像背景色
    getAvatarBackground() {
      // 如果用户设置了自定义背景，使用半透明的橙色
      return 'linear-gradient(135deg, #FF6900 0%, #FF8C42 100%)'
    },
    
    // 获取用户头像背景色
    getUserAvatarBackground() {
      return 'linear-gradient(135deg, #666 0%, #999 100%)'
    },
    // 检查身份设置指令
    checkIdentitySetup(message) {
      const patterns = [
        /现在开始你是([\w\u4e00-\u9fa5]+)/,
        /从现在起你是([\w\u4e00-\u9fa5]+)/,
        /以后你是([\w\u4e00-\u9fa5]+)/,
        /你叫([\w\u4e00-\u9fa5]+)/,
        /你的名字是([\w\u4e00-\u9fa5]+)/,
        /设置身份为([\w\u4e00-\u9fa5]+)/,
        /扮演([\w\u4e00-\u9fa5]+)/
      ]
      
      for (const pattern of patterns) {
        const match = message.match(pattern)
        if (match) {
          return match[1]
        }
      }
      return null
    },

    // 更新身份提示（基于角色设定）
    updateIdentityPrompt() {
      const { characterName, characterAge, characterGender, characterJob, characterPersonality, characterStyle } = this.settings
      
      if (characterName || characterAge || characterGender || characterJob || characterPersonality || characterStyle) {
        let prompt = '记住，你的角色设定：'
        if (characterName) prompt += `名字是${characterName}。`
        if (characterAge) prompt += `年龄${characterAge}岁。`
        if (characterGender) prompt += `性别${characterGender}。`
        if (characterJob) prompt += `职业是${characterJob}。`
        if (characterPersonality) prompt += `性格${characterPersonality}。`
        if (characterStyle) prompt += `聊天风格${characterStyle}。`
        
        this.identity = prompt
      } else {
        this.identity = ''
      }
    },

    // 发送消息（支持上下文和身份）
    async handleSendMessage(message) {
      // 保存最后一条用户消息
      this.lastUserMessage = message
      
      // 检查是否包含身份设置指令
      const identityMatch = this.checkIdentitySetup(message)
      if (identityMatch) {
        this.identity = identityMatch
        // 保存到设置
        this.settings.identity = identityMatch
        this.saveSettings()
      }

      // 添加用户消息
      this.messages.push({
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      })

      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })

      // 显示思考状态（无论动画是否启用都显示）
      const thinkingMsg = {
        role: 'assistant',
        content: '让我想想...🤔',
        isTyping: true,
        timestamp: new Date().toISOString()
      }
      this.messages.push(thinkingMsg)
      
      // 立即滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })

      try {
        // 调用后端API
        const api = await import('./api/index.js').then(m => m.default)
        
        // 构建请求数据，包含身份信息
        const requestData = {
          message: message,
          conversationId: this.conversationId,
          identity: this.identity
        }
        
        const response = await api.sendWithContext(requestData)
        
        // 更新对话ID
        if (response.data.conversation_id && !this.conversationId) {
          this.conversationId = response.data.conversation_id
        }
        
        // 移除思考状态，添加实际回复
        this.messages.pop() // 移除思考消息
        
        this.messages.push({
          role: 'assistant',
          content: response.data.reply,
          timestamp: new Date().toISOString(),
          suggestions: response.data.suggestions || []
        })

        // 保存到本地历史
        this.saveToHistory()

      } catch (error) {
        console.error('发送消息失败:', error)
        
        // 移除思考状态
        if (this.messages.length > 0 && this.messages[this.messages.length - 1].isTyping) {
          this.messages.pop()
        }
        
        this.messages.push({
          role: 'assistant',
          content: '抱歉😰，我现在有点忙🥱，请稍后再试～😣',
          timestamp: new Date().toISOString()
        })
      }

      // 滚动到底部（确保在所有消息添加后）
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },

    // 重新提问
    handleRetry(message) {
      if (message && message.content) {
        // 如果是AI消息，找到对应的用户消息
        if (message.role === 'assistant') {
          // 找到这个AI消息之前的用户消息
          const messageIndex = this.messages.indexOf(message)
          if (messageIndex > 0) {
            const previousMessage = this.messages[messageIndex - 1]
            if (previousMessage.role === 'user') {
              // 重新发送用户消息
              this.handleSendMessage(previousMessage.content)
              return
            }
          }
        }
        // 如果是用户消息，直接重新发送
        if (message.role === 'user') {
          this.handleSendMessage(message.content)
        }
      }
    },

    // 滚动到底部
    scrollToBottom() {
      const chatArea = this.$refs.chatArea
      if (chatArea) {
        chatArea.scrollTop = chatArea.scrollHeight
      }
    },

    // 快速开始
    quickStart(text) {
      this.handleSendMessage(text)
    },

    // 保存到历史
    saveToHistory() {
      const history = JSON.parse(localStorage.getItem('mimo_history') || '[]')
      history.unshift({
        id: Date.now(),
        title: this.messages[0]?.content || '新对话',
        messages: [...this.messages],
        timestamp: new Date().toISOString()
      })
      
      // 只保留最近20条
      if (history.length > 20) {
        history.pop()
      }
      
      localStorage.setItem('mimo_history', JSON.stringify(history))
    },

    // 加载历史
    loadHistory(historyItem) {
      // 禁用动画加载历史消息
      const originalAnimation = this.settings.animationEnabled
      this.settings.animationEnabled = false
      
      this.messages = [...historyItem.messages]
      this.conversationId = historyItem.id
      this.showHistory = false
      
      this.$nextTick(() => {
        this.scrollToBottom()
        // 恢复动画设置
        setTimeout(() => {
          this.settings.animationEnabled = originalAnimation
        }, 100)
      })
    },

    // 保存设置
    saveSettings() {
      localStorage.setItem('mimo_settings', JSON.stringify(this.settings))
      this.updateIdentityPrompt()
    },

    // 清空对话
    clearConversation() {
      this.messages = []
      this.conversationId = null
      this.showHistory = false
    },

    // 处理设置变化（热更新）
    handleSettingsChange(newSettings) {
      this.settings = { ...this.settings, ...newSettings }
      this.saveSettings()
    }
  },

  // 监听设置变化，实现热更新
  watch: {
    settings: {
      handler(newSettings) {
        // 实时更新身份提示
        this.updateIdentityPrompt()
      },
      deep: true
    }
  },

  mounted() {
    // 检查是否是首次使用
    const isFirstUse = !localStorage.getItem('mimo_used_before')
    if (isFirstUse) {
      localStorage.setItem('mimo_used_before', 'true')
    }
    
    // 加载所有设置
    const savedSettings = JSON.parse(localStorage.getItem('mimo_settings') || '{}')
    if (Object.keys(savedSettings).length > 0) {
      this.settings = { ...this.settings, ...savedSettings }
    }
    
    // 向后兼容：加载身份设置
    if (savedSettings.identity) {
      this.identity = savedSettings.identity
    }
    
    // 如果有角色设定，构建身份提示
    this.updateIdentityPrompt()
  }
}
</script>

<style>
/* 全局样式 */
:root {
  /* 小米品牌色 */
  --mimo-orange: #FF6900;
  --mimo-orange-light: #FF8C42;
  --mimo-orange-dark: #E65C00;
  
  /* 温暖背景色 */
  --bg-warm-1: #FFF8F3;
  --bg-warm-2: #FFF3E8;
  --bg-warm-3: #FFE8D6;
  
  /* 文字色 */
  --text-primary: #2D2D2D;
  --text-secondary: #666666;
  --text-light: #999999;
  
  /* 科技感阴影 */
  --shadow-soft: 0 4px 20px rgba(255, 105, 0, 0.08);
  --shadow-medium: 0 8px 30px rgba(255, 105, 0, 0.12);
  
  /* 圆角 */
  --radius-small: 8px;
  --radius-medium: 16px;
  --radius-large: 24px;
}

body {
  margin: 0;
  padding: 0;
  background: linear-gradient(180deg, var(--bg-warm-1) 0%, #FFFFFF 100%);
  color: var(--text-primary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Helvetica Neue', sans-serif;
}

#app {
  max-width: 480px;
  margin: 0 auto;
  min-height: 100vh;
  background: white;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.05);
}

/* 应用容器 */
.mimo-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

/* 头部导航 */
.mimo-header {
  background: rgba(255, 255, 255, 0.20);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 105, 0, 0.1);
  padding: 16px 20px;
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* 头部新布局 */
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  overflow: hidden;
  background: #676767;
}

.header-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.user-avatar {
  background: linear-gradient(135deg, #666 0%, #999 100%) !important;
}

.mimo-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--mimo-orange);
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.header-icon {
  font-size: 20px;
}

.header-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-actions {
  display: flex;
  gap: 6px;
}

.icon-btn {
  background: none;
  border: none;
  font-size: 20px;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s ease;
}

.icon-btn:hover {
  background: rgba(255, 105, 0, 0.1);
}

/* 主要内容区域 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-area {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  scroll-behavior: smooth;
}

.welcome-message {
  text-align: center;
  padding: 40px 20px;
}

.welcome-card {
  background: linear-gradient(135deg, #FFF8F3 0%, #FFE8D6 100%);
  border-radius: var(--radius-medium);
  padding: 24px;
  border: 1px solid rgba(255, 105, 0, 0.1);
}

.welcome-card h3 {
  color: var(--mimo-orange);
  margin: 0 0 12px 0;
  font-size: 20px;
}

.welcome-card p {
  color: var(--text-secondary);
  margin: 0 0 20px 0;
  line-height: 1.6;
}

.quick-start {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.quick-start button {
  background: white;
  border: 1px solid rgba(255, 105, 0, 0.2);
  border-radius: var(--radius-medium);
  padding: 12px;
  color: var(--mimo-orange);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-start button:hover {
  background: var(--mimo-orange);
  color: white;
  border-color: var(--mimo-orange);
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease;
}

.settings-modal,
.history-modal {
  background: white;
  border-radius: var(--radius-medium);
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  overflow: scroll;
  animation: slideUp 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* 移动端适配 */
@media (max-width: 480px) {
  #app {
    max-width: 100%;
  }
  
  .mimo-header {
    //margin-top: 5%;
    padding: 8% 5% 2% 5%;
  }
  
  .chat-area {
    padding: 12px;
  }
  
  .welcome-card {
    padding: 16px;
  }
}
</style>